import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import CpIcon from "../CpIcon/CpIcon";
import { useRevealOnScroll } from "../../hooks/useRevealOnScroll";
import "../../cpLanding.css";

interface CpLandingShellProps {
  /** Breadcrumb label and browser tab title. */
  title: string;
  children: React.ReactNode;
}

/**
 * Frame for a designed product landing page: the brand stylesheet scope, the
 * "Product Catalog › {title}" breadcrumb and the scroll-reveal motion.
 * Pages render their `<section>`s as children.
 *
 * These pages keep the brand's light palette in dark mode — they are
 * marketing artwork with photography and brand ramps, not app chrome.
 */
const CpLandingShell: React.FC<CpLandingShellProps> = ({ title, children }) => {
  const root = useRef<HTMLDivElement>(null);
  useRevealOnScroll(root);

  return (
    <div
      className="cp-landing"
      ref={root}
      style={{
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
        background: "var(--white)",
      }}
    >
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <nav
        aria-label="Breadcrumb"
        style={{
          background: "var(--cream-100)",
          borderBottom: "1px solid var(--border-default)",
        }}
      >
        <div
          style={{
            maxWidth: "var(--container-max)",
            margin: "0 auto",
            padding: "14px 24px",
            display: "flex",
            alignItems: "center",
            gap: 10,
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          <Link
            style={{ color: "var(--grey-500)", textDecoration: "none" }}
            to="/catalog"
          >
            Product Catalog
          </Link>
          <CpIcon color="var(--grey-300)" name="chevron-right" size={14} />
          <span
            aria-current="page"
            style={{ color: "var(--ink-500)", fontWeight: 700 }}
          >
            {title}
          </span>
        </div>
      </nav>
      <main style={{ flex: 1 }}>{children}</main>
    </div>
  );
};

export default CpLandingShell;
